import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Loading } from "../Loading/Loading";
import { RadioButton } from "./RadioButton";
import {
  BakeDate,
  BakeName,
  BakeTime,
  Border,
  Email,
  EmailContainer,
  ErrorMessage,
  FormContainer,
  FormGroup,
  Input,
  Label,
  SubmitButton,
  SubText,
  Text,
} from "./RegisterCard.styles";
import { Button } from "../../styling/card";

interface RegisterCardProps {
  sessionID: number;
  onWaitingList: boolean;
}

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  member: boolean;
  exampleRequired: string;
}

interface Event {
  name: string;
  date: string;
  time: string;
}

interface ServerResponseProps {
  isLoading: boolean;
  error?: string;
  isSuccess?: boolean;
  onWaitingList?: boolean;
  event?: Event;
  currentPosition?: number;
}

const serverURL =
  process.env.REACT_APP_USE_REAL_BACKEND === "true"
    ? process.env.REACT_APP_REAL_BACKEND
    : process.env.REACT_APP_LOCAL_BACKEND;

export const RegisterCard: React.FC<RegisterCardProps> = ({
  sessionID,
  onWaitingList,
}) => {
  const [serverResponse, setServerResponse] = useState<ServerResponseProps>({
    isLoading: false,
  });

  const { register, handleSubmit, errors, reset } = useForm<RegisterForm>();

  const onSubmit = (data: RegisterForm) => {
    setServerResponse({ isLoading: true });
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: `${data.email}@bham.ac.uk`,
        member: data.member,
        session: sessionID,
      }),
    };
    fetch(`${serverURL}/register`, requestOptions)
      .then((response) => response.json())
      .then((data) => validateForm(data))
      .catch((error) => console.log(error));
  };

  const validateForm = (data: ServerResponseProps) => {
    setServerResponse(data);
    if (data) {
      reset();
    }
  };

  if (serverResponse.isSuccess) {
    return (
      <FormContainer>
        <Text>
          {serverResponse.onWaitingList
            ? `You are at position ${serverResponse.currentPosition} on the waiting list.`
            : "Thanks for signing up to this week's session!"}
        </Text>
        <Border>
          <BakeTime>{serverResponse.event?.time} Bake</BakeTime>
          <BakeDate>{serverResponse.event?.date}</BakeDate>
          <BakeName>{serverResponse.event?.name} </BakeName>
        </Border>
        {serverResponse.onWaitingList ? (
          <>
            <SubText>
              You will be sent an email when a space becomes available.
            </SubText>
          </>
        ) : (
          <>
            <SubText>Don't forget to bring a container with you!</SubText>
            <SubText>
              If you can no longer attend, please click the link on the email
              we've just sent to you.
            </SubText>
          </>
        )}
        <Button onClick={() => (window.location.href = "/")} type="button">
          Home
        </Button>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormGroup onSubmit={handleSubmit(onSubmit)}>
        <Label htmlFor="firstName">First Name:</Label>
        <Input
          name="firstName"
          id="firstName"
          ref={register({ required: true, maxLength: 30 })}
        />

        {errors.firstName && (
          <ErrorMessage>This field is required</ErrorMessage>
        )}

        <Label htmlFor="lastName">Last Name:</Label>
        <Input
          name="lastName"
          id="lastName"
          ref={register({ required: true, maxLength: 30 })}
        />

        {errors.lastName && <ErrorMessage>This field is required</ErrorMessage>}

        <Label htmlFor="email">University Email:</Label>
        <EmailContainer>
          <Input
            type="text"
            id="email"
            placeholder="ABC123"
            name="email"
            maxLength={7}
            ref={register({ required: true, minLength: 6 })}
          />
          <Email>@bham.ac.uk</Email>
        </EmailContainer>

        {errors.email?.type === "required" && (
          <ErrorMessage>This field is required</ErrorMessage>
        )}

        {errors.email?.type === "minLength" && (
          <ErrorMessage>Please enter your 6/7 character student ID</ErrorMessage>
        )}

        <Label>Are you a member?</Label>
        <RadioButton name="member" value="true" forwardRef={register}>
          Yes
        </RadioButton>
        <RadioButton name="member" value="false" forwardRef={register}>
          No
        </RadioButton>

        {serverResponse.error && (
          <ErrorMessage>Error: {serverResponse.error}</ErrorMessage>
        )}

        <SubmitButton type="submit">
          {onWaitingList ? "Join Waiting List" : "Register"}
        </SubmitButton>
      </FormGroup>

      {serverResponse.isLoading && <Loading />}
    </FormContainer>
  );
};
